import React from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  CheckCircle,
  Award,
  Target,
  TrendingUp,
  FileText,
} from 'lucide-react';
import { SEO } from '../../../shared/components';

// --- Data Structures ---
interface CurriculumBoard {
    name: string;
    fullName: string;
    icon: React.ReactNode;
    subjects: string[];
    description: string;
}

interface CurriculumApproach {
    icon: React.ReactNode;
    title: string;
    description: string;
}

const curricula: CurriculumBoard[] = [
    {
        name: "CBSE",
        fullName: "Central Board of Secondary Education",
        icon: <BookOpen className="w-6 h-6 text-accent" />,
        description: "Comprehensive CBSE tutoring in Al Wakra for students preparing for board exams. Expert guidance from qualified M.Sc., B.Ed. teachers.",
        subjects: [
            "Mathematics",
            "Physics",
            "Chemistry",
            "Biology",
            "English",
            "Hindi",
            "Social Sciences",
            "Business Studies",
            "Accountancy",
            "Economics",
        ],
    },
    {
        name: "IGCSE",
        fullName: "International General Certificate of Secondary Education",
        icon: <Award className="w-6 h-6 text-accent" />,
        description: "IGCSE tutoring in Qatar with Cambridge-certified approach. Proven results with A* grades in board examinations.",
        subjects: [
            "Mathematics",
            "Physics",
            "Chemistry",
            "Biology",
            "English",
            "Business Studies",
            "Economics",
            "ICT",
            "Global Perspectives",
        ],
    },
    {
        name: "IB",
        fullName: "International Baccalaureate",
        icon: <Target className="w-6 h-6 text-accent" />,
        description: "IB Diploma Programme tutoring in Al Wakra with expert guidance on TOK, Extended Essay, and all IB subjects.",
        subjects: [
            "Mathematics",
            "Physics",
            "Chemistry",
            "Biology",
            "English",
            "Business Management",
            "Economics",
            "Theory of Knowledge",
            "Extended Essay",
        ],
    },
    {
        name: "Federal Board",
        fullName: "Federal Board Curriculum",
        icon: <FileText className="w-6 h-6 text-accent" />,
        description: "Federal Board tutoring in Qatar for students following Pakistani curriculum with experienced teachers.",
        subjects: [
            "Mathematics",
            "Physics",
            "Chemistry",
            "Biology",
            "English",
            "Social Studies",
            "Computer Science",
        ],
    },
    {
        name: "Bangladesh Board",
        fullName: "Bangladesh Board Curriculum",
        icon: <BookOpen className="w-6 h-6 text-accent" />,
        description: "Bangladesh Board tutoring in Al Wakra covering all subjects for SSC and HSC examinations.",
        subjects: [
            "Mathematics",
            "Physics",
            "Chemistry",
            "Biology",
            "English",
            "Bangla",
            "Social Science",
            "Religion",
        ],
    },
];

const approaches: CurriculumApproach[] = [
    {
        icon: <Target className="w-6 h-6 text-accent" />,
        title: "Curriculum Mapping",
        description: "Detailed alignment with CBSE, IGCSE, IB, Federal, and Bangladesh Board syllabi ensuring complete coverage of all topics for board exams in Qatar.",
    },
    {
        icon: <Award className="w-6 h-6 text-accent" />,
        title: "Exam-Focused Preparation",
        description: "Targeted coaching for board examinations with past papers, mock tests, and proven strategies for CBSE, IGCSE, and IB success in Al Wakra.",
    },
    {
        icon: <BookOpen className="w-6 h-6 text-accent" />,
        title: "Supplementary Materials",
        description: "Custom worksheets, study guides, and practice questions designed by experienced teachers to reinforce learning in all curricula.",
    },
    {
        icon: <CheckCircle className="w-6 h-6 text-accent" />,
        title: "Cross-Curriculum Expertise",
        description: "Teachers qualified in multiple educational systems providing comprehensive support for students transitioning between curricula in Qatar.",
    },
    {
        icon: <TrendingUp className="w-6 h-6 text-accent" />,
        title: "Regular Assessment",
        description: "Continuous evaluation through tests and assignments to monitor K-12 student progress and identify areas for improvement.",
    },
    {
        icon: <FileText className="w-6 h-6 text-accent" />,
        title: "Updated Resources",
        description: "Teaching materials regularly updated to reflect latest CBSE, IGCSE, IB, Federal, and Bangladesh Board syllabus changes.",
    },
];

const CurriculumCard: React.FC<{ curriculum: CurriculumBoard, delay: number }> = ({ curriculum, delay }) => (
    <div className={`bg-white rounded-xl shadow-lg text-foreground p-8 animate-slide-up animation-delay-${delay}`}>
        <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 rounded-full bg-cyan-100 flex items-center justify-center flex-shrink-0">
                {curriculum.icon}
            </div>
            <div>
                <h3 className="text-2xl font-display font-bold text-primary">{curriculum.name}</h3>
                <p className="text-sm text-muted-foreground">{curriculum.fullName}</p>
            </div>
        </div>

        <p className="text-muted-foreground mb-6">{curriculum.description}</p>

        <h4 className="font-display font-bold mb-3">Subjects Covered:</h4>
        <div className="grid grid-cols-2 gap-2">
            {curriculum.subjects.map((subject, index) => (
                <div key={index} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-accent flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">{subject}</span>
                </div>
            ))}
        </div>
    </div>
);

const ApproachCard: React.FC<{ approach: CurriculumApproach, delay: number }> = ({ approach, delay }) => (
    <div className={`bg-white rounded-xl shadow-lg text-foreground p-6 animate-slide-up animation-delay-${delay}`}>
        <div className="w-14 h-14 rounded-full bg-cyan-100 flex items-center justify-center mb-4">
            {approach.icon}
        </div>
        <h3 className="text-xl font-display font-bold mb-3">{approach.title}</h3>
        <p className="text-muted-foreground">{approach.description}</p>
    </div>
);

const CurriculumPage: React.FC = () => {
    return (
        <div className="bg-accent text-white min-h-screen">
            <SEO
                title="Curriculum - CBSE, IGCSE, IB, Federal & Bangladesh Board | Tuition Point Qatar"
                description="Complete curriculum support at Tuition Point Al Wakra: CBSE, IGCSE, IB, Federal Board, and Bangladesh Board tutoring. Expert K-12 teachers for all subjects in Qatar."
                keywords="CBSE curriculum Qatar, IGCSE syllabus Al Wakra, IB programme Qatar, Federal Board tutoring, Bangladesh Board Qatar, curriculum support Qatar, board exam preparation, multi-curriculum tutoring Qatar"
            />

            {/* --------------------- Page Header --------------------- */}
            <section className="pt-16 pb-12 md:pt-20 md:pb-16 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="text-center max-w-3xl mx-auto">
                        <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-6 animate-slide-up">
                            Curriculum Support - Al Wakra, Qatar
                        </h1>
                        <p className="text-lg text-white/80 animate-slide-up">
                            Expert tutoring for CBSE, IGCSE, IB, Federal Board, and Bangladesh Board students from kindergarten to grade 12
                        </p>
                    </div>
                </div>
            </section>

            {/* --------------------- Overview --------------------- */}
            <section className="py-24 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="max-w-4xl mx-auto text-center mb-16">
                        <h2 className="text-4xl font-display font-semibold text-white mb-4 animate-slide-up">Multi-Curriculum Excellence in Qatar</h2>
                        <p className="text-lg text-white/80 animate-slide-up">
                            Tuition Point Education Center in Al Wakra provides comprehensive support for multiple international and local educational curricula. Our M.Sc., B.Ed. qualified teachers specialize in CBSE, IGCSE, IB, Federal, and Bangladesh Board systems with proven results in board examinations.
                        </p>
                    </div>
                </div>
            </section>

            {/* --------------------- Supported Curricula --------------------- */}
            <section className="py-24 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-4xl font-display font-semibold text-white mb-4 animate-slide-up">Supported Curricula in Al Wakra</h2>
                        <p className="text-lg text-white/80 animate-slide-up">
                            Comprehensive K-12 subject coverage for all major educational boards in Qatar
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {curricula.map((curriculum, index) => (
                            <CurriculumCard key={index} curriculum={curriculum} delay={(index % 2) + 1} />
                        ))}
                    </div>
                </div>
            </section>

            {/* --------------------- Our Approach --------------------- */}
            <section className="py-24 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-4xl font-display font-semibold text-white mb-4 animate-slide-up">Our Teaching Approach - Qatar</h2>
                        <p className="text-lg text-white/80 animate-slide-up">
                            Proven methods for curriculum delivery and board exam preparation in Al Wakra
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {approaches.map((approach, index) => (
                            <ApproachCard key={index} approach={approach} delay={(index % 3) + 1} />
                        ))}
                    </div>
                </div>
            </section>

            {/* --------------------- Why Multiple Curricula --------------------- */}
            <section className="py-24 bg-accent text-white">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="max-w-4xl mx-auto">
                        <h2 className="text-3xl md:text-4xl font-display font-bold mb-8 text-center animate-slide-up">
                            Why Choose Multi-Curriculum Tutoring in Al Wakra?
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="bg-white rounded-xl p-6 animate-slide-up">
                                <h3 className="text-xl font-display font-bold mb-3 text-primary">Expert Teachers</h3>
                                <p className="text-muted-foreground">
                                    Our teachers in Qatar hold advanced degrees (M.Sc., B.Ed., M.Tech) and specialize in multiple curricula including CBSE, IGCSE, and IB systems.
                                </p>
                            </div>

                            <div className="bg-white rounded-xl p-6 animate-slide-up animation-delay-1">
                                <h3 className="text-xl font-display font-bold mb-3 text-primary">Smooth Transitions</h3>
                                <p className="text-muted-foreground">
                                    Support students transitioning between different educational systems in Al Wakra with our cross-curriculum expertise.
                                </p>
                            </div>

                            <div className="bg-white rounded-xl p-6 animate-slide-up animation-delay-2">
                                <h3 className="text-xl font-display font-bold mb-3 text-primary">Board Exam Success</h3>
                                <p className="text-muted-foreground">
                                    Proven track record with 93%+ average scores in CBSE, IGCSE, and IB board examinations across Qatar.
                                </p>
                            </div>

                            <div className="bg-white rounded-xl p-6 animate-slide-up animation-delay-3">
                                <h3 className="text-xl font-display font-bold mb-3 text-primary">Personalized Learning</h3>
                                <p className="text-muted-foreground">
                                    Customized study plans for each student based on their curriculum, learning style, and academic goals in Qatar.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* --------------------- CTA Section --------------------- */}
            <section className="py-24 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="max-w-3xl mx-auto text-center">
                        <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-6 animate-slide-up">
                            Start Your Curriculum Journey in Al Wakra
                        </h2>
                        <p className="text-lg text-white/80 mb-8 animate-slide-up">
                            Expert support for CBSE, IGCSE, IB, Federal, and Bangladesh Board students in Qatar
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up">
                            <Link
                                to="/enrollment"
                                className="bg-white text-accent hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg shadow-xl transition duration-300 sm:w-auto text-lg"
                            >
                                Enroll Today
                            </Link>
                            <Link
                                to="/contact"
                                className="border-2 border-white text-white hover:bg-white hover:text-accent font-semibold py-3 px-8 rounded-lg transition duration-300 sm:w-auto text-lg"
                            >
                                Contact Us
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default CurriculumPage;
